<template>
  <div>
      <!-- Page Header -->
      <div class="mb-6">
        <h1 class="text-2xl font-bold text-gray-900">{{ $t('navigation.patients') }}</h1>
      </div>

      <!-- Search bar -->
      <div class="flex items-center justify-end mb-6">
        <div class="relative">
          <input 
            v-model="searchQuery"
            @input="debouncedSearch"
            type="text" 
            :placeholder="$t('patient.searchPatientsPlaceholder')"
            class="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          >
          <svg class="w-5 h-5 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
        </div>
      </div>

      <!-- Patients table -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200">
        <div v-if="loading" class="flex justify-center p-8">
          <HeartbeatLoader :text="$t('common.loading')" />
        </div>

        <div v-else-if="error" class="p-6 text-center text-red-600">
          {{ error }}
        </div>

        <div v-else-if="filteredPatients.length === 0" class="p-8 text-center">
          <svg class="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
          </svg>
          <p class="text-gray-600">{{ $t('messages.noPatientsFound') }}</p>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="min-w-full">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('common.fullName') }}
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('common.dateOfBirth') }}
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('common.age') }}
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('common.iin') }}
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('common.gender') }}
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('patient.address') }}
                </th>
                <th v-if="userType === 'nurse'" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('common.actions') }}
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr 
                v-for="patient in filteredPatients" 
                :key="patient.id"
                @click="goToPatientDetails(patient)"
                class="hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <div class="w-10 h-10 rounded-full overflow-hidden mr-3 border-2 border-gray-200">
                      <img 
                        v-if="patient.face_photo" 
                        :src="patient.face_photo" 
                        :alt="getPatientName(patient)"
                        class="w-full h-full object-cover"
                        @error="onImageError"
                      />
                      <div v-else class="w-full h-full bg-blue-100 flex items-center justify-center">
                        <svg class="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                        </svg>
                      </div>
                    </div>
                    <div>
                      <div class="text-sm font-medium text-gray-900">
                        {{ getPatientName(patient) }}
                      </div>
                      <div class="text-sm text-gray-500">
                        ID: {{ patient.id }}
                      </div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ formatDate(patient.birth_date) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <span class="font-medium">{{ calculateAge(patient.birth_date) }}</span>
                  <span class="text-xs text-gray-500 ml-1">{{ $t('time.years') }}</span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ patient.iin }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-semibold" :class="patient.gender === 'M' ? 'text-blue-600' : 'text-red-600'">
                  {{ patient.gender === 'M' ? $t('common.male') : $t('common.female') }}
                </td>
                <td class="px-6 py-4 text-sm text-gray-900 max-w-xs">
                  <div class="whitespace-pre-line" :title="getPatientAddress(patient)">
                    {{ getPatientAddress(patient) }}
                  </div>
                </td>
                <td v-if="userType === 'nurse'" class="px-6 py-4 whitespace-nowrap text-sm font-medium" @click.stop>
                  <button 
                    @click="createVideoCall(patient)"
                    class="text-green-600 hover:text-green-900 transition-colors"
                  >
                    {{ $t('nurse.callPatient') }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <!-- Pagination -->
        <div v-if="filteredPatients.length" class="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div class="flex items-center justify-between">
            <div class="text-sm text-gray-500">
              {{ $t('common.showing') }} 
              <span class="font-medium">{{ (currentPage - 1) * pageSize + 1 }}</span> - 
              <span class="font-medium">{{ Math.min(currentPage * pageSize, totalCount) }}</span> 
              {{ $t('common.of') }} 
              <span class="font-medium">{{ totalCount }}</span>
            </div>
            
            <div class="flex items-center space-x-2">
              <button
                @click="goToPage(currentPage - 1)"
                :disabled="currentPage === 1"
                class="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
              >
                ← {{ $t('common.back') }}
              </button>
              
              <span class="px-3 py-1 text-sm">{{ currentPage }} / {{ totalPages }}</span>
              
              <button
                @click="goToPage(currentPage + 1)"
                :disabled="currentPage >= totalPages"
                class="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
              >
                {{ $t('common.next') }} →
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Patient Details Modal -->
      <div v-if="showPatientModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div class="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-xl bg-white">
          <div class="mt-3">
            <!-- Modal Header -->
            <div class="flex justify-between items-center pb-4 border-b">
              <h3 class="text-lg font-semibold text-gray-900">
                {{ $t('patient.patientInfo') }}
              </h3>
              <button 
                @click="closePatientModal"
                class="text-gray-400 hover:text-gray-600"
              >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <!-- Modal Content -->
            <div class="mt-6" v-if="selectedPatient">
              <!-- Tabs Navigation -->
              <div class="border-b border-gray-200">
                <nav class="-mb-px flex space-x-8">
                  <button 
                    @click="activeTab = 'info'"
                    :class="[
                      'py-2 px-1 border-b-2 font-medium text-sm',
                      activeTab === 'info' 
                        ? 'border-blue-500 text-blue-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    ]"
                  >
                    {{ $t('patient.personalInfo') }}
                  </button>
                  <button 
                    @click="activeTab = 'tracker'"
                    :class="[
                      'py-2 px-1 border-b-2 font-medium text-sm',
                      activeTab === 'tracker' 
                        ? 'border-blue-500 text-blue-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    ]"
                  >
                    {{ $t('patient.trackerRecords') }}
                  </button>
                  <button 
                    @click="activeTab = 'charts'"
                    :class="[
                      'py-2 px-1 border-b-2 font-medium text-sm',
                      activeTab === 'charts' 
                        ? 'border-blue-500 text-blue-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    ]"
                  >
                    {{ $t('patient.charts') }}
                  </button>
                </nav>
              </div>

              <!-- Tab Content -->
              <div class="mt-6">
                <!-- Patient Info Tab -->
                <div v-if="activeTab === 'info'" class="bg-gray-50 rounded-lg p-6">
                  <h4 class="text-lg font-medium text-gray-900 mb-4">{{ $t('patient.personalInfo') }}</h4>
                  <div class="space-y-3">
                    <div>
                      <span class="text-sm font-medium text-gray-500">{{ $t('common.fullName') }}:</span>
                      <p class="text-sm text-gray-900">{{ getPatientName(selectedPatient) }}</p>
                    </div>
                    <div>
                      <span class="text-sm font-medium text-gray-500">{{ $t('common.dateOfBirth') }}:</span>
                      <p class="text-sm text-gray-900">{{ formatDate(selectedPatient.birth_date) }}</p>
                    </div>
                    <div>
                      <span class="text-sm font-medium text-gray-500">{{ $t('common.iin') }}:</span>
                      <p class="text-sm text-gray-900">{{ selectedPatient.iin }}</p>
                    </div>
                    <div>
                      <span class="text-sm font-medium text-gray-500">{{ $t('common.gender') }}:</span>
                      <p class="text-sm font-semibold" :class="selectedPatient.gender === 'M' ? 'text-blue-600' : 'text-red-600'">
                        {{ selectedPatient.gender === 'M' ? $t('common.male') : $t('common.female') }}
                      </p>
                    </div>
                    <div v-if="selectedPatientDetails">
                      <span class="text-sm font-medium text-gray-500">{{ $t('common.phone') }}:</span>
                      <p class="text-sm text-gray-900">{{ selectedPatientDetails.user?.phone || $t('common.notSpecified') }}</p>
                    </div>
                    <div v-if="getPatientAddress(selectedPatient) !== $t('common.addressNotSpecified')">
                      <span class="text-sm font-medium text-gray-500">{{ $t('common.address') }}:</span>
                      <p class="text-sm text-gray-900 whitespace-pre-line">{{ getPatientAddress(selectedPatient) }}</p>
                    </div>
                  </div>
                </div>

                <!-- Health Tracker Records Tab -->
                <div v-if="activeTab === 'tracker'" class="bg-blue-50 rounded-lg p-6">
                  <h4 class="text-lg font-medium text-gray-900 mb-4">{{ $t('patient.trackerRecords') }}</h4>
                  
                  <div v-if="healthTrackerLoading" class="flex justify-center p-4">
                    <div class="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-600"></div>
                  </div>

                  <div v-else-if="healthTrackerError" class="text-red-600 text-sm">
                    {{ healthTrackerError }}
                  </div>

                  <div v-else-if="healthTrackerEntries.length === 0" class="text-gray-500 text-sm">
                    {{ $t('patient.trackerRecordsEmpty') }}
                  </div>

                  <div v-else class="space-y-4">
                    <div v-for="entry in healthTrackerEntries.slice(0, showAllTrackerEntries ? healthTrackerEntries.length : 5)" 
                         :key="entry.id" 
                         class="bg-white rounded-lg p-4 border border-gray-200">
                      <div class="flex justify-between items-start">
                        <div>
                          <h5 class="font-medium text-gray-900">{{ getMetricTypeName(entry.metric_type) }}</h5>
                          <p class="text-sm text-gray-600 mt-1">{{ getValueDisplay(entry) }}</p>
                          <p v-if="entry.notes" class="text-sm text-gray-500 mt-1">{{ entry.notes }}</p>
                        </div>
                        <span class="text-xs text-gray-500">{{ formatDateTime(entry.recorded_at) }}</span>
                      </div>
                    </div>
                    
                    <div v-if="healthTrackerEntries.length > 5" class="text-center">
                      <button 
                        @click="showAllTrackerEntries = !showAllTrackerEntries"
                        class="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        {{ showAllTrackerEntries ? $t('common.hide') : $t('common.showAll') + ' (' + healthTrackerEntries.length + ')' }}
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Charts Tab -->
                <div v-if="activeTab === 'charts'">
                  <div v-if="healthTrackerLoading" class="flex justify-center p-8">
                    <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
                  </div>

                  <div v-else-if="healthTrackerError" class="text-red-600 text-center p-8">
                    {{ healthTrackerError }}
                  </div>

                  <HealthTrackerCharts 
                    v-else
                    :health-data="healthTrackerEntries"
                  />
                </div>
              </div>

              <!-- Modal Footer -->
              <div class="mt-6 flex justify-end space-x-3">
                <button 
                  @click="closePatientModal"
                  class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  {{ $t('common.close') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  <!-- Toast Notifications -->
  <div v-if="toasts.length > 0" class="fixed top-4 right-4 z-50 space-y-2">
    <div 
      v-for="toast in toasts" 
      :key="toast.id"
      class="max-w-sm bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden transform transition-all duration-300 ease-in-out"
      :class="{
        'translate-x-0 opacity-100': toast.show,
        'translate-x-full opacity-0': !toast.show
      }"
    >
      <div class="flex items-center p-4">
        <div class="flex-shrink-0">
          <div v-if="toast.type === 'success'" class="w-6 h-6 text-green-600">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div v-else-if="toast.type === 'error'" class="w-6 h-6 text-red-600">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div v-else class="w-6 h-6 text-blue-600">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
            </svg>
          </div>
        </div>
        <div class="ml-3 flex-1">
          <p class="text-sm font-medium text-gray-900">{{ toast.title }}</p>
          <p v-if="toast.message" class="text-sm text-gray-600 mt-1">{{ toast.message }}</p>
        </div>
        <div class="ml-4 flex-shrink-0">
          <button 
            @click="removeToast(toast.id)"
            class="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../services/api'
import staffApi from '../services/staffApi'
import HealthTrackerCharts from '../components/HealthTrackerCharts.vue'
import HeartbeatLoader from '@/components/common/HeartbeatLoader.vue'

export default {
  name: 'PatientsList',
  components: {
    HealthTrackerCharts,
    HeartbeatLoader
  },
  data() {
    return {
      patients: [],
      loading: false,
      error: null,
      searchQuery: '',
      showPatientModal: false,
      selectedPatient: null,
      selectedPatientDetails: null,
      healthTrackerEntries: [],
      healthTrackerLoading: false,
      healthTrackerError: null,
      showAllTrackerEntries: false,
      activeTab: 'info',
      userType: null,
      toasts: [],
      // Пагинация
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
      totalPages: 1,
      searchTimer: null
    }
  },
  computed: {
    // Теперь поиск на сервере, фильтрация не нужна
    filteredPatients() {
      return this.patients
    }
  },
  async created() {
    await this.checkUserTypeAndFetchPatients()
  },
  methods: {
    async checkUserTypeAndFetchPatients() {
      try {
        // Сначала проверяем тип пользователя
        const authResponse = await api.checkAuth()
        this.userType = authResponse.data.user?.type
        
        // Загружаем пациентов в зависимости от типа пользователя
        await this.fetchPatients()
      } catch (error) {
        this.error = 'Не удалось определить тип пользователя'
      }
    },
    async fetchPatients() {
      this.loading = true
      this.error = null
      
      try {
        let response
        const params = {
          page: this.currentPage,
          page_size: this.pageSize
        }
        if (this.searchQuery) {
          params.search = this.searchQuery
        }
        
        if (this.userType === 'doctor') {
          response = await api.getDoctorClinicPatients(params)
        } else if (this.userType === 'nurse') {
          response = await api.getNurseClinicPatients(params)
        }
        
        if (response.data.results) {
          this.patients = response.data.results
          this.totalCount = response.data.count || response.data.results.length
          this.totalPages = Math.ceil(this.totalCount / this.pageSize)
        } else if (Array.isArray(response.data)) {
          this.patients = response.data
          this.totalCount = response.data.length
          this.totalPages = 1
        } else {
          this.patients = []
          this.totalCount = 0
          this.totalPages = 1
        }
      } catch (error) {
        this.error = 'Не удалось загрузить список пациентов'
        this.patients = []
        this.totalCount = 0
      } finally {
        this.loading = false
      }
    },
    
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
        this.fetchPatients()
      }
    },
    
    debouncedSearch() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.currentPage = 1
        this.fetchPatients()
      }, 400)
    },
    
    getPatientName(patient) {
      // Используем full_name из API или собираем из отдельных полей
      if (patient.full_name) {
        return patient.full_name
      }
      
      // Фоллбэк на случай если full_name не пришел
      if (patient.user) {
        const parts = []
        if (patient.user.first_name) parts.push(patient.user.first_name)
        if (patient.user.middle_name) parts.push(patient.user.middle_name)
        if (patient.user.last_name) parts.push(patient.user.last_name)
        return parts.join(' ') || 'Пациент'
      }
      
      return 'Неизвестный пациент'
    },
    
    formatDate(dateString) {
      if (!dateString) return 'Не указана'
      const date = new Date(dateString)
      return date.toLocaleDateString('ru-RU')
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return 'Не указано'
      const date = new Date(dateTimeString)
      return date.toLocaleDateString('ru-RU') + ' ' + date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    },
    
    async viewPatient(patient) {
      this.selectedPatient = patient
      this.selectedPatientDetails = null
      this.healthTrackerEntries = []
      this.healthTrackerError = null
      this.showAllTrackerEntries = false
      this.showPatientModal = true

      // Загружаем детальную информацию о пациенте
      try {
        const response = await api.getPatientById(patient.id)
        this.selectedPatientDetails = response.data
      } catch (error) {
      // Error handled silently
      }

      // Загружаем записи трекера здоровья
      await this.fetchHealthTrackerEntries(patient.id)
    },

    async fetchHealthTrackerEntries(patientId) {
      this.healthTrackerLoading = true
      this.healthTrackerError = null

      try {
        // Используем staff API для получения трекеров пациента
        const response = await staffApi.getPatientHealthTrackers(patientId)
        this.healthTrackerEntries = response.data.results || response.data || []
      } catch (error) {
        this.healthTrackerError = 'Не удалось загрузить данные трекера здоровья'
      } finally {
        this.healthTrackerLoading = false
      }
    },

    closePatientModal() {
      this.showPatientModal = false
      this.selectedPatient = null
      this.selectedPatientDetails = null
      this.healthTrackerEntries = []
      this.showAllTrackerEntries = false
      this.activeTab = 'info'
    },

    getMetricTypeName(metricType) {
      const metricTypes = {
        'blood_pressure': 'Артериальное давление',
        'heart_rate': 'Пульс',
        'weight': 'Вес',
        'blood_sugar': 'Сахар в крови',
        'temperature': 'Температура тела',
        'oxygen_saturation': 'Насыщение кислородом'
      }
      return metricTypes[metricType] || metricType
    },

    getValueDisplay(entry) {
      if (entry.metric_type === 'blood_pressure') {
        return `${entry.systolic}/${entry.diastolic} мм рт.ст.`
      } else if (entry.metric_type === 'weight') {
        return `${entry.value} кг`
      } else if (entry.metric_type === 'blood_sugar') {
        return `${entry.value} ммоль/л`
      } else if (entry.metric_type === 'heart_rate') {
        return `${entry.value} уд/мин`
      } else if (entry.metric_type === 'temperature') {
        return `${entry.value} °C`
      } else if (entry.metric_type === 'oxygen_saturation') {
        return `${entry.value}%`
      }
      return entry.value
    },

    async createVideoCall(patient) {
      if (!confirm(`Создать видеозвонок с пациентом ${this.getPatientName(patient)}?`)) {
        return
      }
      
      try {
        this.loading = true
        const response = await api.createNurseVideoCall(patient.id)
        
        if (response.data && response.data.video_room_url) {
          // Открываем видеозвонок в новом окне
          const { openVideoRoomWithLanguage } = await import('@/utils/videoRoom');
          openVideoRoomWithLanguage(response.data.video_room_url, this.$i18n.locale);
          
          // Показываем диалог с возможностью поделиться ссылкой
          setTimeout(() => {
            const shareConfirm = confirm(this.$t('appointment.videoCallCreatedSharePrompt'));
            if (shareConfirm) {
              this.copyRoomLinkToClipboard(response.data.video_room_url);
            }
          }, 1000);
        } else {
          alert('Видеозвонок создан, но ссылка недоступна')
        }
      } catch (error) {
        alert('Не удалось создать видеозвонок')
      } finally {
        this.loading = false
      }
    },

    goToPatientDetails(patient) {
      this.$router.push({ name: 'PatientDetails', params: { id: patient.id } })
    },
    
    async copyRoomLinkToClipboard(roomUrl) {
      try {
        // Добавляем языковой параметр к URL
        const { addLanguageToVideoRoomUrl } = await import('@/utils/videoRoom');
        const urlWithLanguage = addLanguageToVideoRoomUrl(roomUrl, this.$i18n.locale);
        
        // Копируем ссылку в буфер обмена
        await navigator.clipboard.writeText(urlWithLanguage);
        
        // Показываем уведомление об успешном копировании
        this.showNotification(this.$t('appointment.roomLinkCopied'), 'success');
        
      } catch (error) {
        
        // Fallback для браузеров без поддержки clipboard API
        try {
          const { addLanguageToVideoRoomUrl } = await import('@/utils/videoRoom');
          const urlWithLanguage = addLanguageToVideoRoomUrl(roomUrl, this.$i18n.locale);
          
          const textArea = document.createElement('textarea');
          textArea.value = urlWithLanguage;
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          
          this.showNotification(this.$t('appointment.roomLinkCopied'), 'success');
        } catch (fallbackError) {
          alert('Не удалось скопировать ссылку автоматически. Ссылка на комнату: ' + roomUrl);
        }
      }
    },
    
    showNotification(message, type = 'info') {
      // Создаем красивое toast-уведомление
      const toast = {
        id: Date.now() + Math.random(),
        title: type === 'success' ? this.$t('messages.successTitle') : (type === 'error' ? this.$t('messages.errorTitle') : this.$t('messages.infoTitle')),
        message: message,
        type: type,
        show: false
      };
      
      this.toasts.push(toast);
      
      // Показываем уведомление с небольшой задержкой для анимации
      setTimeout(() => {
        toast.show = true;
      }, 100);
      
      // Автоматически убираем уведомление через 5 секунд
      setTimeout(() => {
        this.removeToast(toast.id);
      }, 5000);
      
      // Логируем в консоль
      if (type === 'success') {
      } else if (type === 'error') {
      } else {
      // No action needed
      }
    },
    
    removeToast(id) {
      const toastIndex = this.toasts.findIndex(toast => toast.id === id);
      if (toastIndex > -1) {
        // Сначала скрываем с анимацией
        this.toasts[toastIndex].show = false;
        
        // Затем удаляем из массива
        setTimeout(() => {
          this.toasts = this.toasts.filter(toast => toast.id !== id);
        }, 300);
      }
    },

    getPatientAddress(patient) {
      // Формируем адрес из доступных полей
      const addressParts = []
      
      // Добавляем город в начало, если он есть
      if (patient.city_name) {
        addressParts.push(`г. ${patient.city_name}`)
      }
      
      if (patient.address_district) {
        addressParts.push(`р-н ${patient.address_district}`)
      }
      if (patient.address_street) {
        addressParts.push(`ул. ${patient.address_street}`)
      }
      if (patient.address_house) {
        addressParts.push(`д. ${patient.address_house}`)
      }
      if (patient.address_apartment) {
        addressParts.push(`кв. ${patient.address_apartment}`)
      }
      
      return addressParts.length > 0 ? addressParts.join('\n') : this.$t('common.addressNotSpecified')
    },

    calculateAge(birthDate) {
      if (!birthDate) return 'Неизвестно'
      const today = new Date()
      const birth = new Date(birthDate)
      let age = today.getFullYear() - birth.getFullYear()
      const monthDiff = today.getMonth() - birth.getMonth()
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--
      }
      return age
    },

    onImageError(event) {
      event.target.src = 'https://via.placeholder.com/100'
    }
  }
}
</script>

<style scoped>
/* Additional styles if needed */
</style> 